<script setup lang="ts">
const queryClient = useQueryClient();
await Promise.all([
  queryClient.ensureQueryData(homePageQueryOptions()),
  queryClient.ensureQueryData(latestNewsQueryOptions()),
]);
const { page, entrance, about, team } = useHomePage();
const { data: latestNewsData } = useLatestNews();
const { entrance: contactEntrance } = useContactPage();
const facebookUrl = computed(() => getFacebookUrl(contactEntrance.value));
useSeo({ page });

const latestNewsPage = computed(() => getLatestNewsPage(latestNewsData.value));
const latestNewsAuthorId = computed(() => latestNewsPage.value?.body[0]?.value?.author);

// Prefetch authors for latest news and team members in parallel
const prefetches: Promise<unknown>[] = [];
if (latestNewsAuthorId.value) {
  prefetches.push(queryClient.ensureQueryData(authorQueryOptions(latestNewsAuthorId.value)));
}
if (team.value?.value?.members?.length) {
  prefetches.push(...team.value.value.members.map(memberId =>
    queryClient.ensureQueryData(authorQueryOptions(memberId)),
  ));
}
if (prefetches.length) {
  await Promise.all(prefetches);
}

const dateFormatter = new Intl.DateTimeFormat('da-DK', { dateStyle: 'long' });

// Scroll-reveal refs
const newsRef = ref<HTMLElement>();
const aboutRef = ref<HTMLElement>();
const teamRef = ref<HTMLElement>();
const { isVisible: newsVisible } = useReveal(newsRef);
const { isVisible: aboutVisible } = useReveal(aboutRef, { delay: 100 });
const { isVisible: teamVisible } = useReveal(teamRef, { delay: 100 });
</script>

<template>
  <div v-if="page">
    <!-- Hero -->
    <section class="relative overflow-hidden pt-16 pb-12 md:pt-24 md:pb-16">
      <!-- Decorative blobs -->
      <div class="absolute -top-24 -left-24 w-72 h-72 bg-[var(--color-peach)]/20 rounded-full blur-3xl pointer-events-none" />
      <div class="absolute -bottom-16 -right-16 w-96 h-96 bg-primary-200/50 rounded-full blur-3xl pointer-events-none" />
      <div class="absolute top-1/2 left-1/4 w-48 h-48 bg-[var(--color-terracotta)]/15 rounded-full blur-2xl pointer-events-none" />

      <!-- Car silhouette watermark -->
      <div class="absolute inset-0 flex items-center justify-end pointer-events-none select-none">
        <Logo class="w-[60%] max-w-3xl translate-x-[15%] opacity-[0.08]" />
      </div>

      <div class="relative max-w-6xl mx-auto px-6 text-center">
        <h1
          v-if="entrance?.value?.heading"
          class="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-[var(--color-charcoal)]"
        >
          {{ entrance.value.heading }}
        </h1>
        <!-- eslint-disable-next-line vue/no-v-html cms is safe -->
        <div
          v-if="entrance?.value?.content"
          class="mt-6 text-lg md:text-xl text-[var(--color-charcoal)]/70 max-w-2xl mx-auto text-center cms-prose"
          v-html="entrance.value.content"
        />
        <div
          v-if="entrance?.value?.actions?.length"
          class="mt-10 flex flex-wrap items-center justify-center gap-4"
        >
          <UButton
            v-for="action in entrance.value.actions"
            :key="action.href"
            :to="encodeURI(action.href)"
            :label="action.content"
            :color="action.variant === 'outlined' || action.variant === 'text' ? 'neutral' : 'primary'"
            :variant="action.variant === 'outlined' ? 'outline' : action.variant === 'text' ? 'ghost' : 'solid'"
            size="xl"
            class="rounded-full px-10"
          />
        </div>
      </div>
    </section>

    <!-- Featured News -->
    <section
      v-if="latestNewsPage"
      ref="newsRef"
      class="reveal pt-8 pb-16 md:pt-12 md:pb-24"
      :class="{ visible: newsVisible }"
    >
      <div class="max-w-6xl mx-auto px-6">
        <LazyVisibleNewsArticle
          :page="latestNewsPage"
          :date-formatter="dateFormatter"
          featured
          hydrate-on-visible
        />
      </div>
    </section>

    <!-- About -->
    <section
      v-if="about"
      ref="aboutRef"
      class="reveal py-12 md:py-20"
      :class="{ visible: aboutVisible }"
    >
      <div class="max-w-6xl mx-auto px-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
          <div>
            <p class="text-sm font-bold uppercase tracking-widest text-[var(--color-peach)]">
              {{ about.value.overline }}
            </p>
            <h2 class="mt-3 text-3xl md:text-4xl font-bold text-[var(--color-charcoal)]">
              {{ about.value.heading }}
            </h2>
            <!-- eslint-disable-next-line vue/no-v-html cms is safe -->
            <div
              class="mt-6 text-[var(--color-charcoal)]/70 cms-prose"
              v-html="about.value.content"
            />
            <ul
              v-if="about.value.usps?.length"
              class="mt-8 space-y-3"
            >
              <li
                v-for="usp in about.value.usps"
                :key="usp.content"
                class="flex items-center gap-3 bg-white/60 rounded-xl px-4 py-3 shadow-sm"
              >
                <span class="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 shrink-0">
                  <UIcon
                    name="i-heroicons-check-circle"
                    class="size-5 text-primary-600"
                  />
                </span>
                <span class="text-[var(--color-charcoal)] font-medium">{{ usp.content }}</span>
              </li>
            </ul>
          </div>
          <div v-if="about.value.image">
            <Image
              :image="about.value.image"
              class="rounded-2xl shadow-xl"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Team -->
    <section
      v-if="team"
      ref="teamRef"
      class="reveal py-12 md:py-20"
      :class="{ visible: teamVisible }"
    >
      <Background color="radiant">
        <div class="max-w-6xl mx-auto px-6 py-16">
          <div class="text-center max-w-2xl mx-auto">
            <h2 class="text-sm font-bold uppercase tracking-widest text-primary-600">
              {{ team.value.heading }}
            </h2>
            <!-- eslint-disable-next-line vue/no-v-html cms is safe -->
            <div
              class="mt-4 text-[var(--color-charcoal)]/60 mx-auto cms-prose"
              v-html="team.value.content"
            />
          </div>
          <LazyVisibleTeam
            :members="team.value.members"
            class="mt-12"
            hydrate-on-visible
          />
        </div>
      </Background>
    </section>

    <!-- Facebook -->
    <FacebookSection
      v-if="facebookUrl"
      :url="facebookUrl"
    />
  </div>
</template>
